import React, {useEffect, useRef, useState} from "react";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import {lockedPanelRotationAxisKey} from "./localRotation";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FlipIcon from "@mui/icons-material/Flip";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import KeyboardDoubleArrowUpIcon from "@mui/icons-material/KeyboardDoubleArrowUp";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import {DEFAULT_TEXTURE_SCALE_MM, TEST_TEXTURES} from "./detailTextures";
import {GUIDING_AXIS_COLORS} from "./guidingAxisColors";

const rotateBtnSx = {
    border: (theme) => `1px solid ${theme.palette.primary.stroke}`,
    borderRadius: "6px",
    backgroundColor: "#fff",
    p: 0.5,
};

const axisActionBtnSx = {
    border: (theme) => `1px solid ${theme.palette.primary.stroke}`,
    borderRadius: "6px",
    backgroundColor: "#fff",
    p: 0.25,
    flexShrink: 0,
};

const WORLD_AXIS_COLORS = GUIDING_AXIS_COLORS;

const toolbarCoordTextFieldSx = (theme) => ({
    flex: "1 1 0",
    minWidth: 28,
    m: 0,
    height: "100%",
    display: "flex",
    alignSelf: "stretch",
    "& .MuiOutlinedInput-root": {
        borderRadius: 0,
        flex: 1,
        height: "100%",
        m: 0,
        p: 0,
        "& .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.primary.stroke,
            borderWidth: "1px",
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.primary.stroke,
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.primary.stroke,
            borderWidth: "1px",
        },
        "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.primary.stroke,
        },
    },
    "& .MuiInputBase-input": {
        pl: "1px",
        pr: 0,
        py: 0,
        height: "100%",
        boxSizing: "border-box",
        fontSize: "1rem",
        lineHeight: 1.2,
    },
});

const toolbarAxisLabelSx = (color) => ({
    fontWeight: 700,
    color,
    flex: "0 0 20px",
    width: 20,
    minWidth: 20,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    pl: 0,
});

const toolbarNeutralAxisLabelSx = {
    fontWeight: 700,
    color: "black",
    flex: "0 0 20px",
    width: 20,
    minWidth: 20,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    pl: 0,
};

const toolbarModuleCoordRowMinHeight = 40;

const toolbarAxisRowShellSx = (theme, {disabled = false, selected = false} = {}) => ({
    display: "flex",
    alignItems: "stretch",
    gap: 0.75,
    p: 0,
    borderRadius: "6px",
    backgroundColor: selected ? theme.palette.primary.light : "#fff",
    border: `${selected ? 2 : 1}px solid ${
        selected ? theme.palette.primary.main : theme.palette.primary.stroke
    }`,
    flex: "1 1 0",
    minWidth: 0,
    overflow: "hidden",
    opacity: disabled ? 0.55 : 1,
});

export const resolveCoordinateDraft = (rawValue, currentValue, relative = false) => {
    const text = String(rawValue ?? "").trim();
    const current = Number(currentValue) || 0;
    const expression = text.match(
        /^([+-]?(?:\d+(?:\.\d*)?|\.\d+))\s*([+-])\s*(\d+(?:\.\d*)?|\.\d+)$/,
    );
    if (expression) {
        const left = Number(expression[1]);
        const right = Number(expression[3]);
        const result = expression[2] === "+" ? left + right : left - right;
        return Math.round(result * 1000) / 1000;
    }
    if (relative && /^[+-]/.test(text) && text.length > 1) {
        const delta = Number(text);
        if (Number.isFinite(delta)) {
            return Math.round((current + delta) * 1000) / 1000;
        }
    }
    const absolute = Number(text);
    return Number.isFinite(absolute) ? absolute : current;
};

/** 0 → default (центр грани) → max по кругу; direction +1 вверх, −1 вниз. */
export const cyclePriorityCoordinatePreset = (currentRaw, limits, direction) => {
    const presets = [
        Math.round(Number(limits?.min) || 0),
        Math.round(Number(limits?.default) || 0),
        Math.round(Number(limits?.max) || 0),
    ].filter((value, index, array) => array.indexOf(value) === index);

    if (presets.length === 0) {
        return 0;
    }

    const parsed = Number(String(currentRaw ?? "").trim());
    const current = Number.isFinite(parsed) ? Math.round(parsed) : null;
    let idx = current == null ? -1 : presets.indexOf(current);

    if (idx === -1) {
        if (current == null) {
            const defaultIdx = presets.indexOf(Math.round(Number(limits?.default) || 0));
            idx = defaultIdx === -1 ? 0 : defaultIdx;
        } else if (direction > 0) {
            idx = presets.findIndex((value) => value > current);
            if (idx === -1) {
                idx = 0;
            }
        } else {
            idx = presets.reduce(
                (found, value, index) => (value < current ? index : found),
                -1,
            );
            if (idx === -1) {
                idx = presets.length - 1;
            }
        }
        return presets[idx];
    }

    const nextIdx = (idx + direction + presets.length) % presets.length;
    return presets[nextIdx];
};

const thumbBtnSx = (isActive, disabled) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 0.5,
    p: 0.5,
    m: 0,
    width: "100%",
    minWidth: 76,
    cursor: disabled ? "default" : "pointer",
    border: isActive ? "2px solid" : "1px solid",
    borderColor: isActive ? "primary.main" : "primary.stroke",
    borderRadius: "6px",
    backgroundColor: isActive ? "primary.light" : "#fff",
    opacity: disabled ? 0.55 : 1,
    flexShrink: 0,
    "&:hover": disabled ? undefined : {
        borderColor: "primary.main",
    },
});

/**
 * Панель инструментов внизу левой карточки модуля.
 */
const Module3DToolbar = ({
    hasSelection,
    disabled = false,
    onRotate90,
    onMirror,
    basePointMm = null,
    onBasePointCommit,
    snapEnabled = true,
    onSnapEnabledChange,
    orthoEnabled = false,
    onOrthoEnabledChange,
    showReferenceCube = true,
    onShowReferenceCubeChange,
    showShellTransparent = false,
    onShowShellTransparentChange,
    showCompositionShell = true,
    onShowCompositionShellChange,
    showModuleShells = true,
    onShowModuleShellsChange,
    showHoles = true,
    onShowHolesChange,
    showGrooves = true,
    onShowGroovesChange,
    showOtherMachining = true,
    onShowOtherMachiningChange,
    /** 'none' | 'panel' | 'handle' — какой блок материалов показывать */
    materialMode = "none",
    textures = TEST_TEXTURES,
    selectedTextureId = null,
    onTextureSelect,
    onTextureUpload,
    onTextureEdit,
    onTextureDelete,
    texturesDisabled = false,
    textureRotationDeg = 0,
    onTextureRotate90,
    textureScaleMm = DEFAULT_TEXTURE_SCALE_MM,
    onTextureScaleStep,
    handleMaterials = [],
    selectedHandleMaterialId = null,
    fromFileMaterialPreview = null,
    onHandleMaterialSelect,
    onHandleMaterialAdd,
    onHandleMaterialEdit,
    onHandleMaterialDelete,
    showMachiningToggles = true,
    showReferenceCubeToggle = true,
    showShellTransparentToggle = true,
    showCompositionShellToggle = false,
    showModuleShellsToggle = false,
    selectionHint = "Выберите панель или ручку на сцене: для панели — текстура, для ручки — материал покрытия.",
    /** module — вкладка 3D модуля (блок всегда виден); off — скрыт (сцена композиции и т.д.) */
    detailFreeRotationUi = "off",
    freeRotationSelectionOk = false,
    freeRotationEnabled = false,
    onFreeRotationEnabledChange,
    freeRotationAxis = "z",
    onFreeRotationAxisChange,
    freeRotationAngleDrafts = {x: "0", y: "0", z: "0"},
    freeRotationAppliedAngles = {x: 0, y: 0, z: 0},
    onFreeRotationAngleDraftChange,
    onFreeRotationAngleApply,
    showPriorityMarker3dCoordinates = false,
    priorityMarker3dCoordX = "",
    priorityMarker3dCoordY = "",
    priorityMarker3dCoordLimits = {
        x: {min: 0, max: 0, default: 0},
        y: {min: 0, max: 0, default: 0},
    },
    priorityMarker3dCoordsDisabled = true,
    onPriorityMarker3dCoordXChange,
    onPriorityMarker3dCoordYChange,
    onPriorityMarker3dCoordBlur,
    onPriorityMarker3dCoordCommit,
    onPriorityMarker3dCoordKeyDown,
}) => {
    const toolsDisabled = disabled || !hasSelection;
    const showFreeRotationBlock = detailFreeRotationUi === "module";
    const freeRotationActive = showFreeRotationBlock && freeRotationSelectionOk && !toolsDisabled;
    const textureToolsDisabled = Boolean(texturesDisabled) || materialMode !== "panel";
    const handleMaterialDisabled = Boolean(texturesDisabled) || materialMode !== "handle";
    const fileInputRef = useRef(null);
    const editedCoordinateAxesRef = useRef(new Set());
    const editedFreeAngleAxesRef = useRef(new Set());

    const [baseDraft, setBaseDraft] = useState({x: "0", y: "0", z: "0"});

    useEffect(() => {
        if (!basePointMm) return;
        editedCoordinateAxesRef.current.clear();
        setBaseDraft({
            x: String(basePointMm?.x ?? 0),
            y: String(basePointMm?.y ?? 0),
            z: String(basePointMm?.z ?? 0),
        });
    }, [basePointMm?.x, basePointMm?.y, basePointMm?.z]);

    const patchFreeAngleDraft = (axis, value) => {
        onFreeRotationAngleDraftChange?.(axis, value);
    };

    const commitFreeAngleDraft = (axis) => {
        if (!onFreeRotationAngleApply || !freeRotationActive || !freeRotationEnabled) return;
        const current = Number(freeRotationAppliedAngles?.[axis]) || 0;
        const next = resolveCoordinateDraft(
            freeRotationAngleDrafts?.[axis],
            current,
            editedFreeAngleAxesRef.current.has(axis),
        );
        editedFreeAngleAxesRef.current.delete(axis);
        patchFreeAngleDraft(axis, String(next));
        onFreeRotationAngleApply(axis, next);
    };

    const stepFreeAngle = (axis, direction, event) => {
        if (!onFreeRotationAngleApply || !freeRotationActive || !freeRotationEnabled) return;
        const step = event?.shiftKey ? 10 : (event?.altKey ? 0.1 : 1);
        const current = Number(freeRotationAppliedAngles?.[axis]) || 0;
        const next = Math.round((current + direction * step) * 1000) / 1000;
        editedFreeAngleAxesRef.current.delete(axis);
        patchFreeAngleDraft(axis, String(next));
        onFreeRotationAngleApply(axis, next);
    };

    const coordsDisabled = toolsDisabled || Boolean(freeRotationEnabled);
    const lockedRotationAxis = freeRotationActive
        ? lockedPanelRotationAxisKey(freeRotationAppliedAngles)
        : null;
    const rotationAxisLocked = Boolean(lockedRotationAxis);
    const rotationAxisKey = lockedRotationAxis || freeRotationAxis || "z";
    const rotationAxisLabel = rotationAxisKey.toUpperCase();
    const rotationAxisColor = WORLD_AXIS_COLORS[rotationAxisKey] || WORLD_AXIS_COLORS.z;
    const rotationAngleDraft = freeRotationAngleDrafts?.[rotationAxisKey] ?? "0";
    const rotationAxisPickHint = "Поворот возможен только вокруг одной из осей.";
    const canPickRotationAxis = (axis) => (
        !rotationAxisLocked || axis === lockedRotationAxis
    );

    const commitBaseDraft = (axis) => {
        if (!onBasePointCommit) return;
        const next = {
            x: Number(basePointMm?.x) || 0,
            y: Number(basePointMm?.y) || 0,
            z: Number(basePointMm?.z) || 0,
        };
        next[axis] = resolveCoordinateDraft(
            baseDraft?.[axis],
            basePointMm?.[axis],
            editedCoordinateAxesRef.current.has(axis),
        );
        editedCoordinateAxesRef.current.delete(axis);
        setBaseDraft((prev) => ({...prev, [axis]: String(next[axis])}));
        onBasePointCommit(next);
    };

    const openFilePicker = () => {
        if (textureToolsDisabled) return;
        fileInputRef.current?.click();
    };

    const onFileChange = (event) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file) return;
        onTextureUpload?.(file);
    };

    const showPanelMaterials = materialMode === "panel";
    const showHandleMaterials = materialMode === "handle";

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
                p: 1.5,
                borderRadius: "8px",
                border: (theme) => `1px solid ${theme.palette.primary.stroke}`,
                backgroundColor: "#F5F6FA",
                color: "black",
                fontSize: (theme) => theme.typography.body1.fontSize,
                fontFamily: (theme) => theme.typography.fontFamily,
                lineHeight: (theme) => theme.typography.body1.lineHeight,
                "& .MuiTypography-root": {
                    fontFamily: "inherit",
                    lineHeight: "inherit",
                },
                "& .MuiTypography-root.toolbar-label": {
                    fontSize: "inherit",
                    color: "black",
                },
                "& .MuiTypography-root.toolbar-hint": {
                    fontSize: "0.7rem",
                    lineHeight: 1.35,
                    color: "text.secondary",
                },
                "& .MuiFormControlLabel-label": {
                    fontSize: "inherit",
                    fontFamily: "inherit",
                    color: "black",
                },
            }}
        >
            <Typography sx={{fontWeight: 700}} className="toolbar-label">Инструменты</Typography>

            {!hasSelection && !disabled && selectionHint && (
                <Typography className="toolbar-hint">
                    {selectionHint}
                </Typography>
            )}

            {showPriorityMarker3dCoordinates ? (
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.5,
                        width: "100%",
                        minWidth: 0,
                    }}
                >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        flexWrap: "nowrap",
                        alignItems: "stretch",
                        gap: 1,
                        width: "100%",
                        minWidth: 0,
                    }}
                >
                    <Typography
                        className="toolbar-label"
                        sx={{
                            flexShrink: 0,
                            alignSelf: "center",
                            whiteSpace: "nowrap",
                            pr: 0.25,
                        }}
                    >
                        Координаты приоритета, мм
                    </Typography>
                    <Box
                        sx={{
                            display: "flex",
                            flex: "1 1 0",
                            flexDirection: "row",
                            flexWrap: "nowrap",
                            gap: 1,
                            alignItems: "stretch",
                            minWidth: 0,
                        }}
                    >
                    {[
                        {
                            axis: "x",
                            label: "X",
                            value: priorityMarker3dCoordX,
                            onChange: onPriorityMarker3dCoordXChange,
                            ariaLabel: "Координата X 3D",
                        },
                        {
                            axis: "y",
                            label: "Y",
                            value: priorityMarker3dCoordY,
                            onChange: onPriorityMarker3dCoordYChange,
                            ariaLabel: "Координата Y 3D",
                        },
                    ].map(({axis, label, value, onChange, ariaLabel}) => {
                        const limits = priorityMarker3dCoordLimits?.[axis] ?? {
                            min: 0,
                            max: 0,
                            default: 0,
                        };
                        const stepPresetValue = (direction) => {
                            const nextValue = cyclePriorityCoordinatePreset(value, limits, direction);
                            onPriorityMarker3dCoordCommit?.(axis, nextValue);
                        };
                        return (
                        <Box
                            key={axis}
                            sx={(theme) => ({
                                ...toolbarAxisRowShellSx(theme, {
                                    disabled: priorityMarker3dCoordsDisabled,
                                }),
                                minHeight: toolbarModuleCoordRowMinHeight,
                            })}
                        >
                            <Typography component="span" sx={toolbarNeutralAxisLabelSx}>
                                {label}
                            </Typography>
                            <TextField
                                size="small"
                                margin="none"
                                value={value}
                                disabled={priorityMarker3dCoordsDisabled}
                                onChange={(event) => onChange?.(event.target.value)}
                                onBlur={() => onPriorityMarker3dCoordBlur?.(axis)}
                                onKeyDown={(event) => {
                                    if (event.key === "ArrowUp") {
                                        event.preventDefault();
                                        stepPresetValue(1);
                                        return;
                                    }
                                    if (event.key === "ArrowDown") {
                                        event.preventDefault();
                                        stepPresetValue(-1);
                                        return;
                                    }
                                    onPriorityMarker3dCoordKeyDown?.(event, axis);
                                }}
                                inputProps={{
                                    inputMode: "decimal",
                                    "aria-label": ariaLabel,
                                }}
                                sx={toolbarCoordTextFieldSx}
                            />
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    flexShrink: 0,
                                    justifyContent: "center",
                                    pr: 0.25,
                                }}
                            >
                                <IconButton
                                    size="small"
                                    disabled={priorityMarker3dCoordsDisabled}
                                    onMouseDown={(event) => event.preventDefault()}
                                    onClick={() => stepPresetValue(1)}
                                    title={`Далее: 0 → ${limits.default} → ${limits.max} мм`}
                                    sx={{...axisActionBtnSx, p: 0, borderRadius: "4px 4px 0 0"}}
                                >
                                    <KeyboardDoubleArrowUpIcon fontSize="small"/>
                                </IconButton>
                                <IconButton
                                    size="small"
                                    disabled={priorityMarker3dCoordsDisabled}
                                    onMouseDown={(event) => event.preventDefault()}
                                    onClick={() => stepPresetValue(-1)}
                                    title={`Назад: ${limits.max} → ${limits.default} → 0 мм`}
                                    sx={{...axisActionBtnSx, p: 0, borderRadius: "0 0 4px 4px", mt: "-1px"}}
                                >
                                    <KeyboardDoubleArrowDownIcon fontSize="small"/>
                                </IconButton>
                            </Box>
                        </Box>
                        );
                    })}
                    </Box>
                </Box>
                    <Typography className="toolbar-hint">
                        Введите число или смещение +N / −N к сохранённому значению (Enter или уход с поля).
                        Двойные стрелки — по кругу 0, центр грани и максимум.
                    </Typography>
                </Box>
            ) : null}

            <Box sx={{display: "flex", flexDirection: "column", gap: 0.75}}>
                <Typography className="toolbar-label">
                    Координаты, поворот и зеркало
                </Typography>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        flexWrap: "nowrap",
                        gap: 1,
                        alignItems: "stretch",
                        width: "100%",
                    }}
                >
                    {[
                        {axis: "x", label: "X", color: WORLD_AXIS_COLORS.x},
                        {axis: "y", label: "Y", color: WORLD_AXIS_COLORS.y},
                        {axis: "z", label: "Z", color: WORLD_AXIS_COLORS.z},
                    ].map(({axis, label, color}) => {
                        const axisSelectedForRotation = axis === rotationAxisKey && (
                            freeRotationEnabled || rotationAxisLocked
                        );
                        const axisPickBlocked = !canPickRotationAxis(axis);
                        const row = (
                        <Box
                            role={freeRotationEnabled && !axisPickBlocked ? "button" : undefined}
                            tabIndex={freeRotationEnabled && !axisPickBlocked ? 0 : undefined}
                            onClick={() => {
                                if (!freeRotationEnabled || toolsDisabled || axisPickBlocked) return;
                                onFreeRotationAxisChange?.(axis);
                            }}
                            onKeyDown={(e) => {
                                if (!freeRotationEnabled || toolsDisabled || axisPickBlocked) return;
                                if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    onFreeRotationAxisChange?.(axis);
                                }
                            }}
                            sx={(theme) => ({
                                ...toolbarAxisRowShellSx(theme, {
                                    disabled: axisPickBlocked,
                                    selected: axisSelectedForRotation,
                                }),
                                minHeight: toolbarModuleCoordRowMinHeight,
                                cursor: freeRotationEnabled && !toolsDisabled && !axisPickBlocked
                                    ? "pointer"
                                    : (axisPickBlocked ? "not-allowed" : undefined),
                            })}
                        >
                            <Typography
                                component="span"
                                sx={toolbarAxisLabelSx(color)}
                            >
                                {label}
                            </Typography>
                            <TextField
                                size="small"
                                margin="none"
                                value={baseDraft[axis]}
                                disabled={coordsDisabled}
                                onChange={(e) => {
                                    editedCoordinateAxesRef.current.add(axis);
                                    setBaseDraft((prev) => ({...prev, [axis]: e.target.value}));
                                }}
                                onFocus={() => {
                                    if (!freeRotationEnabled && canPickRotationAxis(axis)) {
                                        onFreeRotationAxisChange?.(axis);
                                    }
                                }}
                                onBlur={() => commitBaseDraft(axis)}
                                onKeyDown={(e) => {
                                    if (e.key !== "Enter") return;
                                    e.preventDefault();
                                    commitBaseDraft(axis);
                                }}
                                inputProps={{
                                    step: "0.001",
                                    "aria-label": `Координата ${label}`,
                                }}
                                sx={toolbarCoordTextFieldSx}
                            />
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "row",
                                    alignItems: "center",
                                    flexShrink: 0,
                                    gap: 0.25,
                                    ml: "auto",
                                    pr: 0.25,
                                }}
                            >
                                <IconButton
                                    size="small"
                                    disabled={coordsDisabled}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onRotate90?.(axis, 1);
                                    }}
                                    title={`Повернуть на +90° вокруг мировой оси ${label}`}
                                    sx={{...axisActionBtnSx, alignSelf: "center"}}
                                >
                                    <RotateRightIcon fontSize="small"/>
                                </IconButton>
                                <IconButton
                                    size="small"
                                    disabled={coordsDisabled}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onMirror?.(axis);
                                    }}
                                    title={`Зеркально отразить относительно плоскости, перпендикулярной оси ${label}`}
                                    sx={{...axisActionBtnSx, alignSelf: "center"}}
                                >
                                    <FlipIcon fontSize="small"/>
                                </IconButton>
                            </Box>
                        </Box>
                        );
                        if (!axisPickBlocked) {
                            return <React.Fragment key={axis}>{row}</React.Fragment>;
                        }
                        return (
                            <Tooltip key={axis} title={rotationAxisPickHint}>
                                <Box
                                    component="span"
                                    sx={{
                                        display: "flex",
                                        flex: "1 1 0",
                                        minWidth: 0,
                                    }}
                                >
                                    {row}
                                </Box>
                            </Tooltip>
                        );
                    })}
                </Box>
                {showFreeRotationBlock && freeRotationSelectionOk ? (
                    <>
                        {!freeRotationEnabled ? (
                            <Typography
                                component="button"
                                type="button"
                                disabled={disabled || !hasSelection}
                                onClick={() => onFreeRotationEnabledChange?.(true)}
                                sx={{
                                    border: "none",
                                    background: "none",
                                    p: 0,
                                    m: 0,
                                    textAlign: "left",
                                    cursor: "pointer",
                                    color: "primary.main",
                                    fontSize: "inherit",
                                    fontFamily: "inherit",
                                    lineHeight: 1.35,
                                    textDecoration: "underline",
                                    "&:hover": {color: "primary.dark"},
                                    "&:disabled": {
                                        color: "text.disabled",
                                        cursor: "default",
                                        textDecoration: "none",
                                    },
                                }}
                            >
                                Повернуть вдоль оси…
                            </Typography>
                        ) : (
                            <>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    flexWrap: "wrap",
                                    width: "100%",
                                }}
                            >
                                <Typography component="span" sx={{lineHeight: 1.35}}>
                                    Повернуть вдоль оси
                                </Typography>
                                <Typography
                                    component="span"
                                    sx={{fontWeight: 700, color: rotationAxisColor, lineHeight: 1.35}}
                                >
                                    {rotationAxisLabel}
                                </Typography>
                                <TextField
                                    size="small"
                                    margin="none"
                                    value={rotationAngleDraft}
                                    disabled={toolsDisabled}
                                    onChange={(e) => {
                                        editedFreeAngleAxesRef.current.add(rotationAxisKey);
                                        patchFreeAngleDraft(rotationAxisKey, e.target.value);
                                    }}
                                    onBlur={() => commitFreeAngleDraft(rotationAxisKey)}
                                    onKeyDown={(e) => {
                                        if (e.key === "ArrowUp") {
                                            e.preventDefault();
                                            stepFreeAngle(rotationAxisKey, 1, e);
                                            return;
                                        }
                                        if (e.key === "ArrowDown") {
                                            e.preventDefault();
                                            stepFreeAngle(rotationAxisKey, -1, e);
                                            return;
                                        }
                                        if (e.key !== "Enter") return;
                                        e.preventDefault();
                                        commitFreeAngleDraft(rotationAxisKey);
                                    }}
                                    inputProps={{
                                        step: "0.1",
                                        "aria-label": `Угол поворота вокруг оси ${rotationAxisLabel}`,
                                    }}
                                    sx={(theme) => ({
                                        width: 88,
                                        m: 0,
                                        flexShrink: 0,
                                        "& .MuiOutlinedInput-root": {
                                            backgroundColor: "#fff",
                                            height: 32,
                                        },
                                        "& .MuiInputBase-input": {
                                            p: "0 8px",
                                            fontSize: "1rem",
                                            textAlign: "right",
                                        },
                                        "& .MuiOutlinedInput-notchedOutline": {
                                            borderColor: theme.palette.primary.stroke,
                                        },
                                    })}
                                />
                            </Box>
                            <Typography
                                component="button"
                                type="button"
                                disabled={disabled || !hasSelection}
                                onClick={() => onFreeRotationEnabledChange?.(false)}
                                sx={{
                                    border: "none",
                                    background: "none",
                                    p: 0,
                                    m: 0,
                                    mt: 0.25,
                                    textAlign: "left",
                                    cursor: "pointer",
                                    color: "primary.main",
                                    fontSize: "inherit",
                                    fontFamily: "inherit",
                                    lineHeight: 1.35,
                                    textDecoration: "underline",
                                    "&:hover": {color: "primary.dark"},
                                    "&:disabled": {
                                        color: "text.disabled",
                                        cursor: "default",
                                        textDecoration: "none",
                                    },
                                }}
                            >
                                Перейти в обычный режим…
                            </Typography>
                            </>
                        )}
                    </>
                ) : null}
                {!freeRotationSelectionOk && hasSelection && !disabled && showFreeRotationBlock ? (
                    <Typography className="toolbar-hint">
                        Свободный поворот только для панели на сцене (не ручка и не встраиваемый модуль).
                    </Typography>
                ) : null}
                <Typography className="toolbar-hint">
                    Координаты базовой точки в мм. Допишите +8 или −8 к текущему значению и нажмите Enter. Поворот — 90° вокруг оси куба. Зеркало — через плоскость ⊥ оси.
                    {showFreeRotationBlock && freeRotationEnabled
                        ? " Ось поворота — выделенная строка координаты выше; угол — в градусах (↑/↓, Enter). Двигать деталь — в обычном режиме."
                        : ""}
                </Typography>
            </Box>

            <Box sx={{display: "flex", flexDirection: "column", gap: 0.5}}>
                <FormControlLabel
                    disabled={disabled}
                    control={
                        <Checkbox
                            checked={Boolean(snapEnabled)}
                            onChange={(e) => onSnapEnabledChange?.(e.target.checked)}
                        />
                    }
                    label="Привязка"
                />
                <Typography className="toolbar-hint">
                    Притягивает к контуру модуля (пол/стенки) и к торцам деталей при отпускании; во время драга — слабее и можно отвести от стыка.
                </Typography>
                <FormControlLabel
                    disabled={disabled}
                    control={
                        <Checkbox
                            checked={Boolean(orthoEnabled)}
                            onChange={(e) => onOrthoEnabledChange?.(e.target.checked)}
                        />
                    }
                    label="Орто"
                />
                <Typography className="toolbar-hint">
                    Перемещение по любой из осей X/Y/Z; диагонали отключены.
                </Typography>
                {showReferenceCubeToggle && (
                    <>
                        <FormControlLabel
                            disabled={disabled}
                            control={
                                <Checkbox
                                    checked={Boolean(showReferenceCube)}
                                    onChange={(e) => onShowReferenceCubeChange?.(e.target.checked)}
                                />
                            }
                            label="Габариты модуля"
                        />
                        <Typography className="toolbar-hint">
                            Показывает зелёный ориентир размеров модуля.
                        </Typography>
                    </>
                )}
                {showCompositionShellToggle && (
                    <>
                        <FormControlLabel
                            disabled={disabled}
                            control={
                                <Checkbox
                                    checked={Boolean(showCompositionShell)}
                                    onChange={(e) => onShowCompositionShellChange?.(e.target.checked)}
                                />
                            }
                            label="Оболочка композиции"
                        />
                        <Typography className="toolbar-hint">
                            Зелёный куб габаритов всей композиции.
                        </Typography>
                    </>
                )}
                {showModuleShellsToggle && (
                    <>
                        <FormControlLabel
                            disabled={disabled}
                            control={
                                <Checkbox
                                    checked={Boolean(showModuleShells)}
                                    onChange={(e) => onShowModuleShellsChange?.(e.target.checked)}
                                />
                            }
                            label="Оболочки модулей"
                        />
                        <Typography className="toolbar-hint">
                            Зелёные кубы всех модулей на сцене. Снимите галочку, чтобы видеть только детали.
                        </Typography>
                    </>
                )}
                {showShellTransparentToggle && (
                    <>
                        <FormControlLabel
                            disabled={disabled}
                            control={
                                <Checkbox
                                    checked={Boolean(showShellTransparent)}
                                    onChange={(e) => onShowShellTransparentChange?.(e.target.checked)}
                                />
                            }
                            label="Оболочка"
                        />
                        <Typography className="toolbar-hint">
                            Панели почти прозрачные — видно внутренности модуля.
                        </Typography>
                    </>
                )}
                {showMachiningToggles && (
                    <>
                        <FormControlLabel
                            disabled={disabled}
                            control={
                                <Checkbox
                                    checked={Boolean(showHoles)}
                                    onChange={(e) => onShowHolesChange?.(e.target.checked)}
                                />
                            }
                            label="Отверстия"
                        />
                        <Typography className="toolbar-hint">
                            Сверловка на детали (сквозные, глухие, торцевые) — как «отверстие» в 2D.
                        </Typography>
                        <FormControlLabel
                            disabled={disabled}
                            control={
                                <Checkbox
                                    checked={Boolean(showGrooves)}
                                    onChange={(e) => onShowGroovesChange?.(e.target.checked)}
                                />
                            }
                            label="Пазы"
                        />
                        <Typography className="toolbar-hint">
                            Пазы на пласти и с торца — как «паз» в 2D.
                        </Typography>
                        <FormControlLabel
                            disabled={disabled}
                            control={
                                <Checkbox
                                    checked={Boolean(showOtherMachining)}
                                    onChange={(e) => onShowOtherMachiningChange?.(e.target.checked)}
                                />
                            }
                            label="Прочие элементы"
                        />
                        <Typography className="toolbar-hint">
                            Вырезы, круглые контуры, четверти (и сходная обработка) — не отверстия и не пазы.
                        </Typography>
                    </>
                )}
            </Box>

            {showPanelMaterials && (
            <Box sx={{display: "flex", flexDirection: "column", gap: 0.75}}>
                <Typography className="toolbar-label" sx={{fontWeight: 700}}>
                    Текстура
                </Typography>
                <Typography className="toolbar-hint">
                    Выберите образец, отредактируйте/удалите или загрузите свой (+).
                    Без текстур деталь будет однотонной.
                </Typography>

                {textures.length === 0 && (
                    <Typography className="toolbar-hint" sx={{fontStyle: "italic"}}>
                        Каталог пуст — детали без текстуры. Нажмите «+», чтобы добавить.
                    </Typography>
                )}

                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif,image/bmp,.jpg,.jpeg,.png,.webp"
                    hidden
                    onChange={onFileChange}
                />

                <Box
                    sx={{
                        display: "flex",
                        flexWrap: "nowrap",
                        gap: 1,
                        overflowX: "auto",
                        pb: 0.5,
                        maxWidth: "100%",
                        WebkitOverflowScrolling: "touch",
                    }}
                >
                    {textures.map((tex) => {
                        const isActive = selectedTextureId === tex.id;
                        return (
                            <Box
                                key={tex.id}
                                sx={{
                                    position: "relative",
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    minWidth: 76,
                                    flexShrink: 0,
                                }}
                            >
                                <Box
                                    component="button"
                                    type="button"
                                    disabled={textureToolsDisabled}
                                    onClick={() => onTextureSelect?.(tex)}
                                    title={tex.name}
                                    sx={thumbBtnSx(isActive, textureToolsDisabled)}
                                >
                                    <Box
                                        sx={{
                                            width: 56,
                                            height: 56,
                                            borderRadius: "4px",
                                            overflow: "hidden",
                                            display: "block",
                                            flexShrink: 0,
                                            backgroundColor: "#d0d0d0",
                                        }}
                                    >
                                        <Box
                                            component="img"
                                            src={tex.url}
                                            alt={tex.name}
                                            sx={{
                                                width: "100%",
                                                height: "100%",
                                                objectFit: "cover",
                                                display: "block",
                                                transform: (() => {
                                                    const zoom = Math.max(1, Number(tex.previewZoom) || 1);
                                                    const rot = isActive
                                                        ? Number(textureRotationDeg) || 0
                                                        : 0;
                                                    return `scale(${zoom}) rotate(${rot}deg)`;
                                                })(),
                                                transformOrigin: "center center",
                                                transition: "transform 0.15s ease",
                                                filter: Number(tex.previewZoom) > 1
                                                    ? "contrast(1.35) saturate(1.05)"
                                                    : "none",
                                            }}
                                        />
                                    </Box>
                                    <Typography
                                        sx={{
                                            fontSize: "0.75rem",
                                            lineHeight: 1.2,
                                            textAlign: "center",
                                            color: "black",
                                            maxWidth: 68,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {tex.name}
                                    </Typography>
                                </Box>
                                {isActive && (
                                    <IconButton
                                        size="small"
                                        disabled={textureToolsDisabled}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            onTextureRotate90?.();
                                        }}
                                        title="Повернуть рисунок на 90°"
                                        sx={{
                                            position: "absolute",
                                            top: 2,
                                            right: 2,
                                            p: 0.35,
                                            zIndex: 1,
                                            backgroundColor: "rgba(255,255,255,0.95)",
                                            border: (theme) => `1px solid ${theme.palette.primary.main}`,
                                            boxShadow: "0 1px 2px rgba(0,0,0,0.15)",
                                            "&:hover": {backgroundColor: "#fff"},
                                        }}
                                    >
                                        <RotateRightIcon sx={{fontSize: 18}}/>
                                    </IconButton>
                                )}
                                <Box
                                    sx={{
                                        display: "flex",
                                        gap: 0.25,
                                        mt: 0.25,
                                    }}
                                >
                                    <IconButton
                                        size="small"
                                        disabled={textureToolsDisabled}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onTextureEdit?.(tex);
                                        }}
                                        title="Редактировать"
                                        sx={{p: 0.25}}
                                    >
                                        <EditOutlinedIcon sx={{fontSize: 16}}/>
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        disabled={textureToolsDisabled}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onTextureDelete?.(tex);
                                        }}
                                        title="Удалить"
                                        sx={{p: 0.25}}
                                    >
                                        <DeleteOutlineIcon sx={{fontSize: 16}}/>
                                    </IconButton>
                                </Box>
                            </Box>
                        );
                    })}

                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            minWidth: 76,
                            flexShrink: 0,
                        }}
                    >
                        <Box
                            component="button"
                            type="button"
                            disabled={textureToolsDisabled}
                            onClick={openFilePicker}
                            title="Загрузить свою текстуру"
                            sx={{
                                ...thumbBtnSx(false, textureToolsDisabled),
                                justifyContent: "center",
                                minHeight: 88,
                            }}
                        >
                            <Box
                                sx={{
                                    width: 56,
                                    height: 56,
                                    borderRadius: "4px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    backgroundColor: "#EEF0F5",
                                    border: (theme) => `1px dashed ${theme.palette.primary.stroke}`,
                                }}
                            >
                                <AddIcon sx={{fontSize: 28, color: "text.secondary"}}/>
                            </Box>
                            <Typography
                                sx={{
                                    fontSize: "0.75rem",
                                    lineHeight: 1.2,
                                    textAlign: "center",
                                    color: "black",
                                    maxWidth: 68,
                                }}
                            >
                                Своя
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        flexWrap: "wrap",
                        mt: 0.5,
                        p: 1,
                        borderRadius: "6px",
                        backgroundColor: "#fff",
                        border: (theme) => `1px solid ${theme.palette.primary.stroke}`,
                    }}
                >
                    <Typography className="toolbar-label" sx={{fontWeight: 600}}>
                        Рисунок
                    </Typography>
                    <IconButton
                        size="small"
                        disabled={textureToolsDisabled}
                        onClick={() => onTextureRotate90?.()}
                        title="Повернуть на 90°"
                        sx={rotateBtnSx}
                    >
                        <RotateRightIcon fontSize="small"/>
                    </IconButton>
                    <IconButton
                        size="small"
                        disabled={textureToolsDisabled}
                        onClick={() => onTextureScaleStep?.(1)}
                        title="Крупнее (растянуть)"
                        sx={rotateBtnSx}
                    >
                        <ZoomInIcon fontSize="small"/>
                    </IconButton>
                    <IconButton
                        size="small"
                        disabled={textureToolsDisabled}
                        onClick={() => onTextureScaleStep?.(-1)}
                        title="Мельче (сжать)"
                        sx={rotateBtnSx}
                    >
                        <ZoomOutIcon fontSize="small"/>
                    </IconButton>
                    <Typography className="toolbar-hint">
                        {Math.round(Number(textureScaleMm) || DEFAULT_TEXTURE_SCALE_MM)} мм/повтор
                        {" · "}
                        {Number(textureRotationDeg) || 0}°
                    </Typography>
                </Box>

                <Typography className="toolbar-hint">
                    Образец меняет материал. «+» — картинка с компьютера. Поворот и лупы — рисунок на детали.
                </Typography>
            </Box>
            )}

            {showHandleMaterials && (
            <Box sx={{display: "flex", flexDirection: "column", gap: 0.75}}>
                <Typography className="toolbar-label" sx={{fontWeight: 700}}>
                    Материал ручки
                </Typography>
                <Typography className="toolbar-hint">
                    Выберите покрытие или «Из файла». Карандаш у «Из файла» — править цвет на основе 3DS (создаст свой материал).
                </Typography>
                <Box
                    sx={{
                        display: "flex",
                        flexWrap: "nowrap",
                        gap: 1,
                        overflowX: "auto",
                        pb: 0.5,
                        maxWidth: "100%",
                        WebkitOverflowScrolling: "touch",
                    }}
                >
                    {handleMaterials.map((mat) => {
                        const isActive = selectedHandleMaterialId === mat.id;
                        const isFromFile = Boolean(mat.fromFile);
                        const previewColor = isFromFile
                            ? (fromFileMaterialPreview?.color || mat.color || "#B0B0B0")
                            : mat.color;
                        const previewMap = isFromFile
                            ? (fromFileMaterialPreview?.mapUrl || null)
                            : mat.mapUrl;
                        const previewMetal = isFromFile
                            ? (fromFileMaterialPreview?.metalness ?? 0.45)
                            : mat.metalness;
                        return (
                            <Box
                                key={mat.id}
                                sx={{
                                    position: "relative",
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    minWidth: 76,
                                    width: 76,
                                    flexShrink: 0,
                                }}
                            >
                                <Box
                                    component="button"
                                    type="button"
                                    disabled={handleMaterialDisabled}
                                    onClick={() => onHandleMaterialSelect?.(mat)}
                                    title={isFromFile
                                        ? "Материал из 3DS (как в файле)"
                                        : mat.name}
                                    sx={thumbBtnSx(isActive, handleMaterialDisabled)}
                                >
                                    <Box
                                        sx={{
                                            width: 56,
                                            height: 56,
                                            borderRadius: "4px",
                                            flexShrink: 0,
                                            overflow: "hidden",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            background: previewMap
                                                ? undefined
                                                : `linear-gradient(145deg, ${previewColor} 0%, rgba(255,255,255,0.55) 42%, ${previewColor} 100%)`,
                                            border: "1px solid rgba(0,0,0,0.12)",
                                            boxShadow: !previewMap && previewMetal > 0.5
                                                ? "inset 0 1px 0 rgba(255,255,255,0.45)"
                                                : "none",
                                        }}
                                    >
                                        {previewMap ? (
                                            <Box
                                                component="img"
                                                src={previewMap}
                                                alt={mat.name}
                                                sx={{
                                                    width: "100%",
                                                    height: "100%",
                                                    objectFit: "cover",
                                                    display: "block",
                                                }}
                                            />
                                        ) : null}
                                    </Box>
                                    <Typography
                                        sx={{
                                            fontSize: "0.75rem",
                                            lineHeight: 1.2,
                                            textAlign: "center",
                                            color: "black",
                                            maxWidth: 68,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {mat.name}
                                    </Typography>
                                </Box>
                                <Box
                                    sx={{
                                        display: "flex",
                                        gap: 0.25,
                                        mt: 0.25,
                                    }}
                                >
                                    <IconButton
                                        size="small"
                                        disabled={handleMaterialDisabled}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onHandleMaterialEdit?.(mat);
                                        }}
                                        title={isFromFile
                                            ? "Править на основе материала из файла"
                                            : "Редактировать"}
                                        sx={{p: 0.25}}
                                    >
                                        <EditOutlinedIcon sx={{fontSize: 16}}/>
                                    </IconButton>
                                    {!isFromFile && (
                                        <IconButton
                                            size="small"
                                            disabled={handleMaterialDisabled}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onHandleMaterialDelete?.(mat);
                                            }}
                                            title="Удалить"
                                            sx={{p: 0.25}}
                                        >
                                            <DeleteOutlineIcon sx={{fontSize: 16}}/>
                                        </IconButton>
                                    )}
                                </Box>
                            </Box>
                        );
                    })}

                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            minWidth: 76,
                            width: 76,
                            flexShrink: 0,
                        }}
                    >
                        <Box
                            component="button"
                            type="button"
                            disabled={handleMaterialDisabled}
                            onClick={() => onHandleMaterialAdd?.()}
                            title="Добавить свой материал"
                            sx={{
                                ...thumbBtnSx(false, handleMaterialDisabled),
                                justifyContent: "center",
                                minHeight: 88,
                            }}
                        >
                            <Box
                                sx={{
                                    width: 56,
                                    height: 56,
                                    borderRadius: "4px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    backgroundColor: "#EEF0F5",
                                    border: (theme) => `1px dashed ${theme.palette.primary.stroke}`,
                                }}
                            >
                                <AddIcon sx={{fontSize: 28, color: "text.secondary"}}/>
                            </Box>
                            <Typography
                                sx={{
                                    fontSize: "0.75rem",
                                    lineHeight: 1.2,
                                    textAlign: "center",
                                    color: "black",
                                    maxWidth: 68,
                                }}
                            >
                                Свой
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>
            )}
        </Box>
    );
};

export default Module3DToolbar;
